/**
 * Shared display helpers.
 *
 * Kept separate from the components so the formatting rules that decide how a
 * date looks on screen are in one place and can be reasoned about on their own.
 */

import {
  formatDate,
  instantToCivil,
  toPersianDigits,
  weekdayName,
  jdnWeekday,
  toJdn,
  type CalendarEvent,
  type CalendarSystem,
  type CivilDate,
  type Lang,
} from "@calendar/shared";

/** Digits follow the interface language, not the calendar the user picked. */
export function n(value: number | string, lang: Lang): string {
  const s = String(value);
  return lang === "fa" ? toPersianDigits(s) : s;
}

export function dayNumber(d: CivilDate, lang: Lang): string {
  return n(d.day, lang);
}

/** Month and year for the toolbar, e.g. "مهر ۱۴۰۵" or "Mehr 1405". */
export function monthTitle(d: CivilDate, calendar: CalendarSystem, lang: Lang): string {
  return formatDate({ year: d.year, month: d.month, day: 1 }, calendar, { lang });
}

export function fullDate(d: CivilDate, calendar: CalendarSystem, lang: Lang): string {
  return formatDate(d, calendar, { lang });
}

export function weekdayOf(d: CivilDate, calendar: CalendarSystem, lang: Lang): string {
  return weekdayName(d, calendar, lang);
}

export function weekdayIndexOf(d: CivilDate, calendar: CalendarSystem): number {
  return jdnWeekday(toJdn(d, calendar));
}

export interface Stamp {
  time: string;
  date: string;
  allDay: boolean;
}

export function stamp(
  iso: string,
  timeZone: string,
  calendar: CalendarSystem,
  lang: Lang,
): Stamp {
  const c = instantToCivil(iso, timeZone, calendar);
  return {
    time: n(`${String(c.hour).padStart(2, "0")}:${String(c.minute).padStart(2, "0")}`, lang),
    date: fullDate(c.date, calendar, lang),
    allDay: false,
  };
}

/** Colour a chip from its category, falling back to a neutral accent. */
export function chipColor(event: CalendarEvent, fallback = "var(--accent)"): string {
  return event.category?.color ?? fallback;
}

export function reminderText(minutes: number, t: (k: string, v?: Record<string, string | number>) => string, n2: (x: number) => string): string {
  if (minutes <= 0) return t("reminder.none");
  if (minutes < 60) return `${n2(minutes)} ${t("reminder.min")}`;
  if (minutes < 1440) return `${n2(Math.round(minutes / 60))} ${t("reminder.hour")}`;
  return `${n2(Math.round(minutes / 1440))} ${t("reminder.day")}`;
}
